import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CloseCircle } from 'iconsax-react';

import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type { PlanKind, RunDetail, RunRow, SourceEntity } from '../types';
import OutcomeChip from './OutcomeChip';
import { entityNoun, errorText, formatDateTime } from './format';

type Tone = 'pass' | 'fail' | 'warn' | 'idle';

interface Props {
  clientUid: string;
  runUid: string | null;
  /** Entity of the run's pipeline, for wording ("load" vs "carrier"). */
  entity?: SourceEntity | null;
  onClose: () => void;
  /** A retry produced a new run row — the list should refresh. */
  onRetried?: (row: RunRow) => void;
}

const PLAN_LABELS: Record<PlanKind, string> = {
  CREATE: 'Create the transaction',
  ADOPT: 'Adopt the existing transaction',
  FLAG: 'Flag for Operations',
  UPDATE: 'Update the transaction',
  NOOP: 'No change',
  CANCEL: 'Cancel the transaction',
  SKIP: 'Skip',
  HOLD: 'Hold until the missing fields arrive'
};

const ACTION_WORDS: Record<string, string> = { create: 'New', update: 'Update to', delete: 'Deletion of' };

const RETRYABLE = new Set(['FAILED', 'HELD']);

function TimelineItem({
  tone,
  title,
  body,
  children,
  last
}: {
  tone: Tone;
  title: string;
  body?: React.ReactNode;
  children?: React.ReactNode;
  last?: boolean;
}) {
  const theme = useTheme();
  const color =
    tone === 'pass'
      ? theme.palette.success.main
      : tone === 'fail'
      ? theme.palette.error.main
      : tone === 'warn'
      ? theme.palette.warning.main
      : theme.palette.grey[500];
  const symbol = tone === 'pass' ? '✓' : tone === 'fail' ? '✕' : tone === 'warn' ? '!' : '–';
  return (
    <Stack direction="row" spacing={1.5} component="li" sx={{ listStyle: 'none' }}>
      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            color,
            bgcolor: alpha(color, 0.12),
            border: `1px solid ${alpha(color, 0.4)}`
          }}
        >
          {symbol}
        </Box>
        {!last && <Box sx={{ width: '1px', flex: 1, minHeight: 12, bgcolor: 'divider', my: 0.5 }} />}
      </Stack>
      <Box sx={{ pb: last ? 0 : 2, minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1">{title}</Typography>
        {body && (
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        )}
        {children}
      </Box>
    </Stack>
  );
}

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 };

export default function RunDetailDrawer({ clientUid, runUid, entity, onClose, onRetried }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useApi();
  const [currentUid, setCurrentUid] = useState<string | null>(runUid);
  const [run, setRun] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => setCurrentUid(runUid), [runUid]);

  useEffect(() => {
    if (!currentUid) {
      setRun(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    diApi
      .getRun(clientUid, currentUid)
      .then((res) => {
        if (cancelled) return;
        if (res.success === false) setError(res.message || 'Could not load this run.');
        else setRun(res.data);
      })
      .catch((err) => !cancelled && setError(errorText(err, 'Could not load this run.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientUid, currentUid, reloadTick]);

  const noun = entityNoun(entity);
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

  const retry = async () => {
    if (!run) return;
    setRetrying(true);
    try {
      const res = await diApi.retryRun(clientUid, run.uid);
      if (res.success === false) {
        showToast(res.message || 'Could not retry this run', 'error');
        return;
      }
      showToast(`${Noun} ${run.recordId} queued for another try.`, 'success');
      if (res.data) {
        onRetried?.(res.data);
        if (res.data.uid && res.data.uid !== currentUid) setCurrentUid(res.data.uid);
        else setReloadTick((t) => t + 1);
      }
    } catch (err) {
      showToast(errorText(err, 'Could not retry this run'), 'error');
    } finally {
      setRetrying(false);
    }
  };

  const openTransaction = () => {
    if (!run?.authkeyUid) return;
    const teamUid = run.evaluation?.target?.teamUid || '_';
    navigate(`/transactions/details/${teamUid}/${run.authkeyUid}/${run.transactionKey || ''}`);
  };

  const renderTimeline = (r: RunDetail) => {
    const ev = r.evaluation;
    const items: React.ReactNode[] = [];
    items.push(
      <TimelineItem
        key="received"
        tone="pass"
        title="Received from Aljex"
        body={
          <>
            {ACTION_WORDS[r.action] ?? 'Change to'} {noun}{' '}
            <Box component="code" sx={mono}>
              {r.recordId}
            </Box>{' '}
            · {formatDateTime(r.createdAt)}
          </>
        }
      />
    );

    if (!ev) {
      items.push(
        <TimelineItem
          key="detail"
          tone={r.outcome === 'FAILED' ? 'fail' : 'idle'}
          title="Outcome"
          body={r.detail || 'No evaluation was stored for this run.'}
        />
      );
      return items;
    }

    const failed = ev.filterTrace.filter((t) => !t.pass).length;
    items.push(
      <TimelineItem
        key="filter"
        tone={ev.matched ? 'pass' : 'fail'}
        title="Filter"
        body={
          ev.matched
            ? ev.filterTrace.length
              ? `All ${ev.filterTrace.length} condition${ev.filterTrace.length === 1 ? '' : 's'} passed`
              : 'No conditions — every record matches'
            : failed
            ? `${failed} of ${ev.filterTrace.length} condition${ev.filterTrace.length === 1 ? '' : 's'} failed`
            : r.detail
        }
      >
        {ev.filterTrace.length > 0 && (
          <Stack component="ul" spacing={0.5} sx={{ pl: 0, mt: 1, mb: 0 }}>
            {ev.filterTrace.map((t, i) => (
              <Stack component="li" key={i} direction="row" spacing={1} sx={{ listStyle: 'none' }}>
                <Typography variant="body2" sx={{ color: t.pass ? 'success.main' : 'error.main', fontWeight: 700, width: 14 }}>
                  {t.pass ? '✓' : '✕'}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{t.label}</Typography>
                  {t.value && (
                    <Typography variant="caption" color="text.secondary" sx={{ ...mono, wordBreak: 'break-word' }}>
                      {t.value}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </TimelineItem>
    );

    if (!ev.matched) {
      items.push(
        <TimelineItem
          key="stopped"
          tone="idle"
          title="Stopped"
          body={`Nothing else ran. The ${noun} is checked again on its next update.`}
          last
        />
      );
      return items;
    }

    items.push(
      <TimelineItem
        key="target"
        tone={ev.target ? 'pass' : 'fail'}
        title="Target"
        body={ev.target ? ev.target.teamName : `No process for this ${noun}`}
      />
    );

    const sourceFields = ev.mapped.filter((m) => m.owner === 'SOURCE');
    const missing = sourceFields.filter((m) => m.missing);
    items.push(
      <TimelineItem
        key="mapping"
        tone={missing.length ? 'warn' : 'pass'}
        title="Mapping"
        body={
          sourceFields.length
            ? `${sourceFields.length - missing.length} of ${sourceFields.length} Aljex field${sourceFields.length === 1 ? '' : 's'}${
                missing.length ? ` · ${missing.map((m) => m.label).join(', ')} missing` : ''
              }`
            : 'No fields are filled from Aljex'
        }
      >
        {ev.mapped.length > 0 && (
          <Box
            sx={{
              mt: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            {ev.mapped.map((m) => (
              <Stack
                key={m.fieldUid}
                direction="row"
                spacing={1.5}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 0 },
                  bgcolor: m.missing ? alpha(theme.palette.warning.main, 0.08) : 'transparent'
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>
                  {m.label}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {m.owner === 'OPERATIONS' ? (
                    <Typography variant="caption" color="text.secondary">
                      Left to Operations
                    </Typography>
                  ) : m.missing ? (
                    <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                      Missing
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {m.display || '—'}
                    </Typography>
                  )}
                  {m.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {m.note}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Box>
        )}
      </TimelineItem>
    );

    const planTone: Tone =
      ev.plan.kind === 'SKIP' || ev.plan.kind === 'NOOP' ? 'idle' : ev.plan.kind === 'FLAG' || ev.plan.kind === 'HOLD' ? 'warn' : 'pass';
    items.push(
      <TimelineItem
        key="plan"
        tone={planTone}
        title="Plan"
        body={
          <>
            <strong>{PLAN_LABELS[ev.plan.kind] ?? ev.plan.kind}</strong>
            {ev.plan.reason ? ` — ${ev.plan.reason}` : ''}
            {ev.plan.existingKey
              ? ` · already tracked as ${ev.plan.existingKey}${ev.plan.existingTeamName ? ` in ${ev.plan.existingTeamName}` : ''}`
              : ''}
          </>
        }
      >
        {ev.warnings?.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {ev.warnings.map((w, i) => (
              <Alert key={i} severity="warning" sx={{ py: 0 }}>
                {w}
              </Alert>
            ))}
          </Stack>
        )}
      </TimelineItem>
    );

    const actions = r.actions ?? [];
    items.push(
      <TimelineItem
        key="actions"
        tone={actions.length === 0 ? 'idle' : actions.some((a) => !a.ok) ? 'fail' : 'pass'}
        title="Actions"
        body={
          actions.length
            ? undefined
            : r.mode === 'SHADOW'
            ? 'Not run — the pipeline is in Shadow mode'
            : r.outcome === 'FAILED'
            ? 'Not run — the run failed before any action'
            : 'Nothing to do'
        }
        last
      >
        {actions.length > 0 && (
          <Stack component="ul" spacing={0.5} sx={{ pl: 0, mt: 0.5, mb: 0 }}>
            {actions.map((a, i) => (
              <Stack component="li" key={i} direction="row" spacing={1} sx={{ listStyle: 'none' }}>
                <Typography variant="body2" sx={{ color: a.ok ? 'success.main' : 'error.main', fontWeight: 700, width: 14 }}>
                  {a.ok ? '✓' : '✕'}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {a.type}
                  </Box>
                  {a.message ? ` · ${a.message}` : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </TimelineItem>
    );
    return items;
  };

  return (
    <Drawer anchor="right" open={Boolean(runUid)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 560 } } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="h5">{run ? `${Noun} ${run.recordId}` : 'Run details'}</Typography>
          <IconButton onClick={onClose} aria-label="Close run details">
            <CloseCircle size={22} />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
          {loading && !run ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress aria-label="Loading run" />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : run ? (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <OutcomeChip outcome={run.outcome} />
                <Typography variant="caption" color="text.secondary">
                  {run.pipelineName} v{run.version} · {run.mode === 'SHADOW' ? 'Shadow' : 'Live'} · {formatDateTime(run.createdAt)}
                  {run.attempts > 1 ? ` · attempt ${run.attempts}` : ''}
                </Typography>
                {loading && <CircularProgress size={14} />}
              </Stack>
              {run.detail && <Typography variant="body2">{run.detail}</Typography>}
              {run.mode === 'SHADOW' && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Shadow run: this shows what would have happened. Nothing was created and nobody was notified.
                </Alert>
              )}
              <Box component="ol" sx={{ m: 0, p: 0 }}>
                {renderTimeline(run)}
              </Box>
              {run.error && (
                <Alert severity="error" sx={{ wordBreak: 'break-word' }}>
                  {run.error}
                </Alert>
              )}
            </Stack>
          ) : null}
        </Box>
        {run && (RETRYABLE.has(run.outcome) || run.authkeyUid) && (
          <Stack direction="row" spacing={1} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            {RETRYABLE.has(run.outcome) && (
              <Button
                variant="contained"
                onClick={retry}
                disabled={retrying}
                startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Retry now
              </Button>
            )}
            {run.authkeyUid && (
              <Button variant="outlined" onClick={openTransaction}>
                Open transaction{run.transactionKey ? ` ${run.transactionKey}` : ''}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}
